import { v, type Infer } from "convex/values";
import {
  UserSessionPayloadSchema,
  type UserSessionPayload,
} from "@bindery/shared/contracts/session";
import type { Assert, Exact } from "@bindery/shared/contracts/pin";

/**
 * Tier-3 mirror of the `user_session` arm of
 * `@bindery/shared/src/contracts/session.ts`, narrowed to the subset this
 * deployment consumes (`docs/data-architecture/README.md` §5).
 *
 * The contract is the source: it is where the broker JWT's claims are
 * described, what `apps/auth` mints against and what
 * `packages/auth-consumer/src/verify.ts` parses verified tokens through.
 * `packages/shared` may not import `convex`, so this validator cannot be
 * generated from it and is hand-written — and pinned to it by
 * {@link SessionPayloadMirrorsContract} below, which fails to compile if the
 * two ever disagree.
 *
 * This is the SUBSET retells uses. The raw token also carries the standard
 * claims (`aud`, `iss`, `exp`, `nbf`, `iat`, `jti`, `scopes`) plus
 * `readerNames` and `orgMemberships`; a raw-JWT parser must tolerate those
 * extra claims rather than validate the token strictly against this object.
 * {@link consumedSessionPayload} is the projection that does that narrowing,
 * and `apps/retells/src/lib/auth.ts` is its caller.
 *
 * SHRANK WITH KANBAN #88. `email`, `lastName`, `profilePictureUrl` and
 * `accessToken` left the contract's `user_session` arm when PII moved to the
 * `enterprise` CockroachDB database, so they cannot be mirrored here — the
 * `Exact` pin below is what turns that into a compile error rather than a
 * validator quietly describing claims no token carries. Nothing in this
 * deployment or in `apps/retells` read the three it consumed, with one
 * documented exception recorded on `glossTokens` in ../schema.ts.
 *
 * `firstName` remains only because the contract kept it deprecated for one
 * release; delete it here in the same change that deletes it there.
 *
 * Discriminator is `typ` (values `"user_session"` | `"reader_session"`);
 * retells uses the WorkOS `user_session` lane. `sub` equals `workosId` for user
 * sessions.
 */
export const sessionPayloadValidator = v.object({
  typ: v.literal("user_session"),
  /** Stable WorkOS user id; the `admins` table keys on this. */
  workosId: v.string(),
  /** Standard subject claim; = `workosId` for a user_session. */
  sub: v.optional(v.string()),
  /** @deprecated PII; removed from the contract once `../app` stops reading it. */
  firstName: v.optional(v.string()),
  /** Currently active reader, when the account has selected one. */
  readerId: v.optional(v.string()),
  readerNickname: v.optional(v.string()),
  /**
   * Broker-side platform role, when present. NOTE: this is the sara-platform
   * internal role, NOT retells admin capability — retells authorizes admin
   * actions via its own `admins` table (SPEC "Auth"). Surfaced here only so a
   * consumer can read it; do not gate retells admin on it without a decision.
   */
  internalRole: v.optional(
    v.union(
      v.literal("founder"),
      v.literal("admin"),
      v.literal("support"),
      v.literal("researcher"),
    ),
  ),
});
export type SessionPayload = Infer<typeof sessionPayloadValidator>;

/**
 * The claims this deployment consumes, named once: the pin below, the
 * browser-side parse and the projection all read this. A `.pick` mask rather
 * than a key list, because that is the shape zod takes without a cast.
 */
const CONSUMED = {
  typ: true,
  workosId: true,
  sub: true,
  firstName: true,
  readerId: true,
  readerNickname: true,
  internalRole: true,
} as const satisfies Partial<Record<keyof UserSessionPayload, true>>;

type ConsumedKey = keyof typeof CONSUMED;
const CONSUMED_KEYS = Object.keys(CONSUMED) as readonly ConsumedKey[];

/**
 * `SessionPayload` and the contract's `user_session` arm restricted to
 * {@link CONSUMED_KEYS} describe the same shape, checked by the compiler.
 *
 * `Exact` is mutual assignability plus a key-set comparison, and the second
 * half is not optional here: every claim but `typ`/`workosId` is optional, and
 * mutual assignability alone admits an optional field added to one side. The
 * reasoning is written once, in `@bindery/shared/contracts/pin`. A divergence
 * fails the build instead of surfacing as a claim silently missing at runtime.
 */
export type SessionPayloadMirrorsContract = Assert<
  Exact<SessionPayload, Pick<UserSessionPayload, ConsumedKey>>
>;

/**
 * The consumed subset as a zod schema, for the one reader of a RAW token in
 * this app — `apps/retells/src/lib/auth.ts`, which decodes the broker JWT in
 * the browser for display and correlation claims. The contract's arm
 * `.pick`ed to {@link CONSUMED}, so a claim retells never reads can neither
 * fail the decode nor survive it. Parsing the whole arm did exactly that: a
 * roster entry in `readerNames` or an org role the broker adds later would
 * have rendered a reader signed out of the page while Convex, which verifies
 * the token itself and never runs this schema, kept the session.
 *
 * `internalRole` is the one consumed claim read leniently. Its enum is the
 * broker's, and retells surfaces the value without authorizing on it (the
 * `admins` table is the gate), so a value this contract does not know is
 * dropped — the way the hand-rolled decoder always treated it — rather than
 * refusing the token.
 */
export const consumedSessionPayloadSchema = UserSessionPayloadSchema.pick(
  CONSUMED,
).extend({
  internalRole: UserSessionPayloadSchema.shape.internalRole.catch(undefined),
});

/**
 * Project a parsed contract payload down to the consumed subset.
 *
 * Pure, and deliberately key-by-key: an absent optional claim must stay absent
 * rather than become a key whose value is `undefined`, because callers compare
 * the result against object literals. `CONSUMED_KEYS` drives the copy, so
 * adding a field to the validator without adding it to the contract is a type
 * error at the pin above rather than a claim that quietly never arrives.
 */
export function consumedSessionPayload(
  payload: Pick<UserSessionPayload, ConsumedKey>,
): SessionPayload {
  const consumed: SessionPayload = {
    typ: payload.typ,
    workosId: payload.workosId,
  };
  for (const key of CONSUMED_KEYS) {
    const value = payload[key];
    if (value !== undefined) {
      (consumed as Record<string, unknown>)[key] = value;
    }
  }
  return consumed;
}
